import { useContext } from 'react';
import { Link, useOutletContext, useParams } from 'react-router-dom';

import Debug from '@/components/Debug';
import AppContext from '@/contexts/appContext';
import { ENCOUNTER_TYPES, Encounter } from '@/types/encounter';
import Button from '@/ui/components/Button';
import Card from '@/ui/components/Card';
import Container from '@/ui/components/Container';
import Heading from '@/ui/components/Heading';
import { ENCOUNTER_VIEW_BETA_TESTERS_GROUP, isInGroup } from '@/utils/userPermission';

import { EncounterPersonDetails } from './components';
import BillingDetails from './components/BillingItem/components/BillingDetails';
import EncounterAppointmentCard from './components/EncounterAppointmentCard';
import NonBillableBillingItem from './components/NonBillableBillingItem';
import RefreshBillingItem from './components/RefreshBillingItem';

const EncounterDetailsHome = () => {
  const { encounter, refetchEncounter } = useOutletContext<{
    encounter: Encounter;
    path: ENCOUNTER_TYPES;
    refetchEncounter: () => void;
  }>();
  const { encounterType } = useParams<{ encounterType: string }>();
  const { permissions } = useContext(AppContext);

  // Gated until the encounter view's member info is ready to launch.
  const showNewCounselorView = isInGroup(permissions, ENCOUNTER_VIEW_BETA_TESTERS_GROUP);

  return (
    <Container type="page">
      {encounter.appointment && <EncounterAppointmentCard encounter={encounter} />}
      <EncounterPersonDetails
        billingItem={encounter.billing_item}
        encounterType={encounterType as ENCOUNTER_TYPES}
      />

      {!showNewCounselorView && (
        <Card className="p-2">
          <div className="flex justify-between mb-4">
            <Heading text="Billing" title="Billing" value={3} />
          </div>

          <NonBillableBillingItem
            billingItem={encounter.billing_item}
            encounterType={encounterType as ENCOUNTER_TYPES}
          />

          {encounter.billing_item ? (
            <div className="pb-4">
              <BillingDetails billingItem={encounter.billing_item} charts={encounter.charts} />
              <RefreshBillingItem
                billingItem={encounter.billing_item}
                refetchEncounter={refetchEncounter}
              />
            </div>
          ) : (
            <Heading value={4} text="No billing item" />
          )}

          <Link to="billing">
            <Button className="w-full">View Details</Button>
          </Link>
        </Card>
      )}

      <Debug object={{ encounter, encounterType }} />
    </Container>
  );
};

export default EncounterDetailsHome;
